'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetServiceTypesQuery } from '@/features/service-types/api/service-typesApi';
import {
  useDeactivateWorkspaceMemberMutation,
  useLazyGetWorkspaceInvitesQuery,
  useLazyGetWorkspaceJoinRequestsQuery,
  useWorkspaceJoinRequestDecisionMutation,
  useLazyGetWorkspaceMembersQuery,
  useReactivateWorkspaceMemberMutation,
  useRevokeWorkspaceInviteMutation,
  useUpdateWorkspaceMemberMutation,
} from '@/features/workspaces/api/workspacesApi';
import { useCanWorkspaceAction } from '@/features/workspaces/hooks';
import { useAppSelector } from '@/store/hooks';
import {
  Divider,
  EmptyState,
  InfiniteTable,
  OptionsMenu,
  Badge,
} from '@web/ui';
import { getDateTimeFormat, getErrorMessage, notify } from '@web/utils';

import { WorkspaceMemberAvatar } from '../WorkspaceMemberAvatar';

import {
  WorkspaceMemberEditDrawer,
  type WorkspaceMemberEditPayload,
} from './WorkspaceMemberEditDrawer';
import styles from './WorkspaceTeamSettingsPanel.module.scss';

import type {
  GetWorkspaceInvitesParams,
  GetWorkspaceJoinRequestsParams,
  GetWorkspaceMembersParams,
  UpdateWorkspaceMemberPayload,
  WorkspaceInviteItem,
  WorkspaceJoinRequestItem,
  WorkspaceJoinRequestDecision,
  WorkspaceMemberItem,
} from '@/features/workspaces/types';
import type { ServiceType } from '@web/types';

type LoadMode = 'replace' | 'append';

type WorkspaceTeamSettingsPanelProps = {
  search: string;
  membersStatus: 'active' | 'suspended';
  role?: string;
  serviceTypeId?: string;
};

function getNextPage(nextUrl: string | null): number | null {
  if (!nextUrl) {
    return null;
  }

  try {
    const parsed = nextUrl.startsWith('http')
      ? new URL(nextUrl)
      : new URL(nextUrl, 'http://localhost');
    const pageValue = parsed.searchParams.get('page');

    if (!pageValue) {
      return null;
    }

    const page = Number(pageValue);
    return Number.isNaN(page) ? null : page;
  } catch {
    return null;
  }
}

function dedupeById<T extends { id: string }>(rows: T[]) {
  const byId = new Map<string, T>();
  rows.forEach((row) => {
    byId.set(row.id, row);
  });
  return Array.from(byId.values());
}

function formatRoleLabel(roleSlug: string) {
  if (!roleSlug) {
    return '-';
  }

  return roleSlug.charAt(0).toUpperCase() + roleSlug.slice(1);
}

function formatCapacityLabel(capacity: number | null | undefined) {
  if (capacity === null || capacity === undefined) {
    return '-';
  }

  return `${capacity}h`;
}

function formatSeatCostLabel(member: WorkspaceMemberItem) {
  const quantity = member.monthly_seat_cost?.quantity;
  const currencyCode = member.monthly_seat_cost?.currency?.code ?? 'USD';

  if (quantity === null || quantity === undefined) {
    return '-';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: Number.isInteger(quantity) ? 0 : 2,
  }).format(quantity);
}

function formatServiceTypesSummary(serviceTypes: ServiceType[]) {
  if (!serviceTypes.length) {
    return '-';
  }

  return `${serviceTypes.length} selected`;
}

export function WorkspaceTeamSettingsPanel({
  search,
  membersStatus,
  role,
  serviceTypeId,
}: WorkspaceTeamSettingsPanelProps) {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const currentUserEmail = currentUser?.email?.trim().toLowerCase() ?? '';
  const canEditMembers = useCanWorkspaceAction(
    'workspace_member_management',
    'edit',
  );
  const canDeleteMembers = useCanWorkspaceAction(
    'workspace_member_management',
    'delete',
  );
  const canManageMemberActions = canEditMembers || canDeleteMembers;
  const canDeleteInvites = useCanWorkspaceAction('workspace_invite', 'delete');
  const canEditInvites = useCanWorkspaceAction('workspace_invite', 'edit');

  const [membersRows, setMembersRows] = useState<WorkspaceMemberItem[]>([]);
  const [invitesRows, setInvitesRows] = useState<WorkspaceInviteItem[]>([]);
  const [joinRequestsRows, setJoinRequestsRows] = useState<
    WorkspaceJoinRequestItem[]
  >([]);

  const [membersNextPage, setMembersNextPage] = useState<number | null>(1);
  const [invitesNextPage, setInvitesNextPage] = useState<number | null>(1);
  const [joinRequestsNextPage, setJoinRequestsNextPage] = useState<
    number | null
  >(1);

  const [editingMember, setEditingMember] =
    useState<WorkspaceMemberItem | null>(null);
  const [isMemberDrawerOpen, setIsMemberDrawerOpen] = useState(false);

  const [getWorkspaceMembers, { isFetching: isMembersFetching }] =
    useLazyGetWorkspaceMembersQuery();
  const [getWorkspaceInvites, { isFetching: isInvitesFetching }] =
    useLazyGetWorkspaceInvitesQuery();
  const [getWorkspaceJoinRequests, { isFetching: isJoinRequestsFetching }] =
    useLazyGetWorkspaceJoinRequestsQuery();

  const [updateWorkspaceMember, { isLoading: isUpdatingMember }] =
    useUpdateWorkspaceMemberMutation();
  const [deactivateWorkspaceMember, { isLoading: isDeactivatingMember }] =
    useDeactivateWorkspaceMemberMutation();
  const [reactivateWorkspaceMember, { isLoading: isReactivatingMember }] =
    useReactivateWorkspaceMemberMutation();
  const [revokeWorkspaceInvite, { isLoading: isRevokingInvite }] =
    useRevokeWorkspaceInviteMutation();
  const [workspaceJoinRequestDecision, { isLoading: isDecidingJoinRequest }] =
    useWorkspaceJoinRequestDecisionMutation();

  const { data: serviceTypesResponse } = useGetServiceTypesQuery({
    page_size: 100,
    show_archived: false,
  });

  const membersBaseParams = useMemo<GetWorkspaceMembersParams>(
    () => ({
      page_size: 25,
      query: search || undefined,
      status: membersStatus,
      role: role || undefined,
      typical_service_types: serviceTypeId || undefined,
    }),
    [membersStatus, role, search, serviceTypeId],
  );

  const invitesBaseParams = useMemo<GetWorkspaceInvitesParams>(
    () => ({
      page_size: 25,
      status: 'active',
    }),
    [],
  );

  const joinRequestsBaseParams = useMemo<GetWorkspaceJoinRequestsParams>(
    () => ({
      page_size: 25,
      status: 'active',
    }),
    [],
  );

  const loadMembersPage = useCallback(
    async (page: number, mode: LoadMode = 'append') => {
      try {
        const response = await getWorkspaceMembers(
          {
            ...membersBaseParams,
            page,
          },
          true,
        ).unwrap();
        const nextRows = response.data?.results ?? [];

        setMembersRows((current) =>
          mode === 'replace' ? nextRows : dedupeById([...current, ...nextRows]),
        );
        setMembersNextPage(getNextPage(response.data?.next ?? null));
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [getWorkspaceMembers, membersBaseParams],
  );

  const loadInvitesPage = useCallback(
    async (page: number, mode: LoadMode = 'append') => {
      try {
        const response = await getWorkspaceInvites(
          {
            ...invitesBaseParams,
            page,
          },
          true,
        ).unwrap();
        const nextRows = response.data?.results ?? [];

        setInvitesRows((current) =>
          mode === 'replace' ? nextRows : dedupeById([...current, ...nextRows]),
        );
        setInvitesNextPage(getNextPage(response.data?.next ?? null));
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [getWorkspaceInvites, invitesBaseParams],
  );

  const loadJoinRequestsPage = useCallback(
    async (page: number, mode: LoadMode = 'append') => {
      try {
        const response = await getWorkspaceJoinRequests(
          {
            ...joinRequestsBaseParams,
            page,
          },
          true,
        ).unwrap();
        const nextRows = response.data?.results ?? [];

        setJoinRequestsRows((current) =>
          mode === 'replace' ? nextRows : dedupeById([...current, ...nextRows]),
        );
        setJoinRequestsNextPage(getNextPage(response.data?.next ?? null));
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [getWorkspaceJoinRequests, joinRequestsBaseParams],
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadMembersPage(1, 'replace');
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [loadMembersPage]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadInvitesPage(1, 'replace');
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [loadInvitesPage]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadJoinRequestsPage(1, 'replace');
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [loadJoinRequestsPage]);

  const availableServiceTypes = serviceTypesResponse?.data?.results ?? [];

  const pendingInvitesRows = useMemo(
    () => invitesRows.filter((invite) => invite.status === 'pending'),
    [invitesRows],
  );

  const pendingJoinRequestsRows = useMemo(
    () => joinRequestsRows.filter((request) => request.status === 'pending'),
    [joinRequestsRows],
  );

  const closeDrawer = useCallback(() => {
    setIsMemberDrawerOpen(false);
  }, []);

  const openEditDrawer = useCallback((member: WorkspaceMemberItem) => {
    setEditingMember(member);
    setIsMemberDrawerOpen(true);
  }, []);

  const handleDrawerExited = useCallback(() => {
    setEditingMember(null);
  }, []);

  const isCurrentUserMember = useCallback(
    (member: WorkspaceMemberItem) => {
      const memberEmail = member.user.email?.trim().toLowerCase() ?? '';

      if (!currentUserEmail || !memberEmail) {
        return false;
      }

      return memberEmail === currentUserEmail;
    },
    [currentUserEmail],
  );

  const handleToggleMemberStatus = async (member: WorkspaceMemberItem) => {
    try {
      if (member.status === 'active') {
        await deactivateWorkspaceMember(member.id).unwrap();
        notify({ type: 'success', message: 'Member deactivated.' });
      } else {
        await reactivateWorkspaceMember(member.id).unwrap();
        notify({ type: 'success', message: 'Member activated.' });
      }

      await loadMembersPage(1, 'replace');

      if (editingMember?.id === member.id) {
        closeDrawer();
      }
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleSaveMember = useCallback(
    async (values: WorkspaceMemberEditPayload) => {
      if (!editingMember) {
        return;
      }

      try {
        const payload: UpdateWorkspaceMemberPayload = {
          role_slug: values.roleSlug,
          monthly_capacity: values.monthlyCapacity,
          monthly_seat_cost:
            values.monthlySeatCost === null
              ? null
              : {
                  quantity: values.monthlySeatCost,
                  currency_code:
                    editingMember.monthly_seat_cost?.currency?.code ?? 'USD',
                },
          typical_service_type_ids: values.typicalServiceTypeIds,
        };

        await updateWorkspaceMember({
          membershipId: editingMember.id,
          payload,
        }).unwrap();

        notify({ type: 'success', message: 'Member updated.' });

        closeDrawer();
        await loadMembersPage(1, 'replace');
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [closeDrawer, editingMember, loadMembersPage, updateWorkspaceMember],
  );

  const handleDeleteInvite = useCallback(
    async (inviteId: string) => {
      try {
        await revokeWorkspaceInvite(inviteId).unwrap();
        notify({ type: 'success', message: 'Invite deleted.' });
        await loadInvitesPage(1, 'replace');
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [loadInvitesPage, revokeWorkspaceInvite],
  );

  const handleJoinRequestDecision = useCallback(
    async (
      joinRequestId: string,
      decision: WorkspaceJoinRequestDecision,
      shouldRefreshMembers = false,
    ) => {
      try {
        const response = await workspaceJoinRequestDecision({
          joinRequestId,
          payload: { decision },
        }).unwrap();
        notify({
          type: 'success',
          message: response.message || 'Join request updated.',
        });
        await loadJoinRequestsPage(1, 'replace');
        if (shouldRefreshMembers) {
          await loadMembersPage(1, 'replace');
        }
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [loadJoinRequestsPage, loadMembersPage, workspaceJoinRequestDecision],
  );

  const membersColumns = [
    {
      id: 'name',
      header: 'Name',
      accessor: (row: WorkspaceMemberItem) =>
        `${row.user.first_name} ${row.user.last_name}`,
      sortable: true,
      cell: (row: WorkspaceMemberItem) => (
        <span className={styles.teamPanel__memberNameCell}>
          <WorkspaceMemberAvatar
            user={row.user}
            roleLabel={formatRoleLabel(row.role.slug)}
            size="small"
            closeOnTriggerMouseLeave
          />
          <strong>
            {row.user.first_name} {row.user.last_name}
          </strong>
        </span>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessor: (row: WorkspaceMemberItem) => row.user.email,
      sortable: true,
      cell: (row: WorkspaceMemberItem) => (
        <span
          className="truncate-md"
          style={{ display: 'block', width: '100%' }}
          title={row.user.email}
        >
          {row.user.email}
        </span>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      width: 80,
      accessor: (row: WorkspaceMemberItem) => formatRoleLabel(row.role.slug),
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      width: 95,
      accessor: (row: WorkspaceMemberItem) => row.status,
      sortable: true,
      cell: (row: WorkspaceMemberItem) => (
        <span className={styles.teamPanel__statusCell}>
          <Badge
            label={formatRoleLabel(row.status)}
            tone={row.status === 'active' ? 'success' : 'warning'}
            size="md"
          />
        </span>
      ),
    },
    {
      id: 'monthly_capacity',
      header: 'Monthly capacity',
      width: 130,
      accessor: (row: WorkspaceMemberItem) => row.monthly_capacity ?? 0,
      cell: (row: WorkspaceMemberItem) =>
        formatCapacityLabel(row.monthly_capacity),
      sortable: true,
    },
    {
      id: 'typical_service_types',
      header: 'Typical service types',
      width: 150,
      accessor: (row: WorkspaceMemberItem) =>
        formatServiceTypesSummary(row.typical_service_types),
      sortable: true,
    },
    {
      id: 'monthly_seat_cost',
      header: 'Monthly seat cost',
      width: 130,
      accessor: (row: WorkspaceMemberItem) =>
        row.monthly_seat_cost?.quantity ?? 0,
      cell: (row: WorkspaceMemberItem) => formatSeatCostLabel(row),
      sortable: true,
    },
    ...(canManageMemberActions
      ? [
          {
            id: 'actions',
            header: '',
            width: 72,
            accessor: (row: WorkspaceMemberItem) => row.id,
            sortable: false,
            cell: (row: WorkspaceMemberItem) => {
              const actions = [
                ...(canEditMembers
                  ? [
                      {
                        key: 'edit-member',
                        label: 'Edit',
                        onSelect: () => openEditDrawer(row),
                      },
                    ]
                  : []),
                ...(canDeleteMembers && !isCurrentUserMember(row)
                  ? [
                      {
                        key: 'toggle-member-status',
                        label:
                          row.status === 'active'
                            ? 'Deactivate member'
                            : 'Activate member',
                        onSelect: () => {
                          void handleToggleMemberStatus(row);
                        },
                      },
                    ]
                  : []),
              ];

              if (!actions.length) {
                return null;
              }

              return (
                <div className={styles.teamPanel__actionsCell}>
                  <OptionsMenu
                    ariaLabel="Team member actions"
                    actions={actions}
                  />
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const invitesColumns = useMemo(
    () => [
      {
        id: 'email',
        header: 'Email',
        accessor: (row: WorkspaceInviteItem) => row.invitee_email,
        sortable: true,
      },
      {
        id: 'role',
        header: 'Role',
        accessor: (row: WorkspaceInviteItem) => formatRoleLabel(row.role.slug),
        sortable: true,
      },
      {
        id: 'invited',
        header: 'Invited',
        accessor: (row: WorkspaceInviteItem) => row.created_at,
        cell: (row: WorkspaceInviteItem) => getDateTimeFormat(row.created_at),
        sortable: true,
      },
      {
        id: 'last_sent',
        header: 'Last sent',
        accessor: (row: WorkspaceInviteItem) => row.created_at,
        cell: (row: WorkspaceInviteItem) => getDateTimeFormat(row.created_at),
        sortable: true,
      },
      ...(canDeleteInvites
        ? [
            {
              id: 'actions',
              header: '',
              accessor: (row: WorkspaceInviteItem) => row.id,
              sortable: false,
              cell: (row: WorkspaceInviteItem) => (
                <div className={styles.teamPanel__actionsCell}>
                  <OptionsMenu
                    ariaLabel="Pending invite actions"
                    actions={[
                      {
                        key: 'delete-invite',
                        label: 'Delete invite',
                        onSelect: () => {
                          void handleDeleteInvite(row.id);
                        },
                        tone: 'danger',
                        loading: isRevokingInvite,
                      },
                    ]}
                  />
                </div>
              ),
            },
          ]
        : []),
    ],
    [canDeleteInvites, handleDeleteInvite, isRevokingInvite],
  );

  const joinRequestsColumns = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: (row: WorkspaceJoinRequestItem) =>
          `${row.requester.first_name} ${row.requester.last_name}`,
        sortable: true,
      },
      {
        id: 'email',
        header: 'Email',
        accessor: (row: WorkspaceJoinRequestItem) => row.requester.email,
        sortable: true,
      },
      {
        id: 'requested',
        header: 'Requested',
        accessor: (row: WorkspaceJoinRequestItem) => row.created_at,
        cell: (row: WorkspaceJoinRequestItem) =>
          getDateTimeFormat(row.created_at),
        sortable: true,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: (row: WorkspaceJoinRequestItem) => row.status,
        sortable: true,
        cell: (row: WorkspaceJoinRequestItem) => (
          <Badge
            label={formatRoleLabel(row.status)}
            tone={row.status === 'pending' ? 'warning' : 'default'}
          />
        ),
      },
      ...(canEditInvites
        ? [
            {
              id: 'actions',
              header: '',
              accessor: (row: WorkspaceJoinRequestItem) => row.id,
              sortable: false,
              cell: (row: WorkspaceJoinRequestItem) => (
                <div className={styles.teamPanel__actionsCell}>
                  <OptionsMenu
                    ariaLabel="Join request actions"
                    actions={[
                      {
                        key: 'approve-join-request',
                        label: 'Approve',
                        loading: isDecidingJoinRequest,
                        onSelect: () => {
                          void handleJoinRequestDecision(
                            row.id,
                            'approve',
                            true,
                          );
                        },
                      },
                      {
                        key: 'deny-join-request',
                        label: 'Deny',
                        loading: isDecidingJoinRequest,
                        onSelect: () => {
                          void handleJoinRequestDecision(row.id, 'deny');
                        },
                      },
                      {
                        key: 'block-join-request',
                        label: 'Block',
                        loading: isDecidingJoinRequest,
                        onSelect: () => {
                          void handleJoinRequestDecision(row.id, 'block');
                        },
                      },
                      {
                        key: 'unblock-join-request',
                        label: 'Unblock',
                        loading: isDecidingJoinRequest,
                        onSelect: () => {
                          void handleJoinRequestDecision(row.id, 'unblock');
                        },
                      },
                    ]}
                  />
                </div>
              ),
            },
          ]
        : []),
    ],
    [canEditInvites, handleJoinRequestDecision, isDecidingJoinRequest],
  );

  const isDrawerProcessing =
    isUpdatingMember || isDeactivatingMember || isReactivatingMember;

  const showMembersEmptyState = !isMembersFetching && membersRows.length === 0;
  const showInvitesEmptyState =
    !isInvitesFetching && pendingInvitesRows.length === 0;
  const showJoinRequestsEmptyState =
    !isJoinRequestsFetching && pendingJoinRequestsRows.length === 0;

  return (
    <div className={styles.teamPanel}>
      <section className={styles.teamPanel__section}>
        <header className={styles.teamPanel__sectionHeader}>
          <h2 className={styles.teamPanel__sectionTitle}>Members</h2>
        </header>

        {showMembersEmptyState ? (
          <EmptyState title="No members found." />
        ) : (
          <InfiniteTable
            tableId="workspace-team-members-table"
            columns={membersColumns}
            rows={membersRows}
            getRowId={(row) => row.id}
            onLoadMore={() => {
              if (membersNextPage && !isMembersFetching) {
                void loadMembersPage(membersNextPage, 'append');
              }
            }}
            hasMore={Boolean(membersNextPage)}
            isLoading={isMembersFetching}
            virtualizedHeight="auto"
          />
        )}
      </section>

      <section className={styles.teamPanel__section}>
        <header className={styles.teamPanel__sectionHeader}>
          <h2 className={styles.teamPanel__sectionTitle}>Pending Invites</h2>
          <span className={styles.teamPanel__sectionMeta}>
            {pendingInvitesRows.length} pending
          </span>
        </header>

        {showInvitesEmptyState ? (
          <EmptyState title="No pending invites." />
        ) : (
          <InfiniteTable
            tableId="workspace-team-pending-invites-table"
            columns={invitesColumns}
            rows={pendingInvitesRows}
            getRowId={(row) => row.id}
            onLoadMore={() => {
              if (invitesNextPage && !isInvitesFetching) {
                void loadInvitesPage(invitesNextPage, 'append');
              }
            }}
            hasMore={Boolean(invitesNextPage)}
            isLoading={isInvitesFetching}
            virtualizedHeight="auto"
          />
        )}
      </section>

      <section className={styles.teamPanel__section}>
        <header className={styles.teamPanel__sectionHeader}>
          <h2 className={styles.teamPanel__sectionTitle}>Join Requests</h2>
          <span className={styles.teamPanel__sectionMeta}>
            {pendingJoinRequestsRows.length} pending
          </span>
        </header>

        {showJoinRequestsEmptyState ? (
          <EmptyState title="No join requests." />
        ) : (
          <InfiniteTable
            tableId="workspace-team-join-requests-table"
            columns={joinRequestsColumns}
            rows={pendingJoinRequestsRows}
            getRowId={(row) => row.id}
            onLoadMore={() => {
              if (joinRequestsNextPage && !isJoinRequestsFetching) {
                void loadJoinRequestsPage(joinRequestsNextPage, 'append');
              }
            }}
            hasMore={Boolean(joinRequestsNextPage)}
            isLoading={isJoinRequestsFetching}
            virtualizedHeight="auto"
          />
        )}
      </section>

      <WorkspaceMemberEditDrawer
        open={isMemberDrawerOpen}
        member={editingMember}
        serviceTypes={availableServiceTypes}
        isProcessing={isDrawerProcessing}
        disableRoleChange={
          editingMember ? isCurrentUserMember(editingMember) : false
        }
        canToggleStatus={
          editingMember
            ? canDeleteMembers && !isCurrentUserMember(editingMember)
            : canDeleteMembers
        }
        onClose={closeDrawer}
        onExited={handleDrawerExited}
        onSave={handleSaveMember}
        onToggleStatus={handleToggleMemberStatus}
      />
    </div>
  );
}
